/**
 * Shared motion design tokens for consistent, premium-feeling animations.
 * Used by multimodal homepage scenes and section-level transitions.
 */

export const motionDurations = {
  entrance: 0.7,
  entranceFast: 0.5,
  loopSlow: 3.5,
  loopMedium: 2.8,
  loopCinematic: 4.2,
  loopFilmstrip: 7.5,
  micro: 0.2,
  pathDraw: 0.9,
  labelCycle: 3.8,
  loopAmbient: 5.5,
  arcFadeDuration: 3.2,
  focusWindowDuration: 6.0,
  /** Memory species grammar durations */
  spatialAlign: 0.45,
  spatialHold: 0.4,
  spatialFragment: 4.8,
  echoPulse: 1.2,
  echoDecay: 2.4,
  temporalDrift: 7.5,
  temporalSkip: 0.15,
} as const;

/** Opacity range for abstract scene ambient elements */
export const abstractOpacityRange = {
  min: 0.4,
  mid: 0.5,
  max: 0.6,
} as const;

/** Blur range for edge decay on translucent planes */
export const abstractBlurRange = {
  soft: '4px',
  mid: '2px',
  sharp: '0px',
} as const;

export const motionEasings = {
  /** Smooth expo-out for entrances and exits */
  premium: [0.23, 1.0, 0.32, 1] as const,
  /** Calm ambient loops */
  calmLoop: 'circInOut' as const,
  linear: 'linear' as const,
  easeOut: 'easeOut' as const,
} as const;

export const memorySpeciesGrammars = {
  stillBloom: {
    align: motionDurations.spatialAlign,
    hold: motionDurations.spatialHold,
    fragment: motionDurations.spatialFragment,
    easing: motionEasings.premium,
    loopEasing: motionEasings.calmLoop,
  },
  echoThread: {
    pulse: motionDurations.echoPulse,
    decay: motionDurations.echoDecay,
    easing: motionEasings.calmLoop,
  },
  timeCurrent: {
    drift: motionDurations.temporalDrift,
    skip: motionDurations.temporalSkip,
    easing: motionEasings.linear,
    loopEasing: motionEasings.calmLoop,
  },
} as const;

export const motionStagger = {
  default: 0.15,
  cards: 0.15,
  path: 0.15,
  nodes: 0.12,
} as const;

export const motionSprings = {
  gentle: { type: 'spring' as const, stiffness: 300, damping: 30 },
  node: { type: 'spring' as const, stiffness: 280, damping: 20 },
} as const;

export const motionViewport = {
  once: true,
  amount: 0.15,
  margin: '0px 0px -60px 0px' as const,
} as const;

export const motionTokens = {
  durations: motionDurations,
  easings: motionEasings,
  stagger: motionStagger,
  springs: motionSprings,
  viewport: motionViewport,
  species: memorySpeciesGrammars,
} as const;

export type MotionTokens = typeof motionTokens;

/** Scale loop duration when card is hovered (legacy ambient scenes) */
export function hoverLoopMultiplier(isHovered?: boolean): number {
  return isHovered ? 1.18 : 1;
}

/** Sharpen/stabilize semantics when card is hovered (memory species scenes) */
export function sharpenOnHover(isHovered?: boolean) {
  return {
    alignDurationFactor: isHovered ? 0.75 : 1,
    holdDurationFactor: isHovered ? 1.6 : 1,
    driftDurationFactor: isHovered ? 1.2 : 1,
    echoDecayFactor: isHovered ? 0.65 : 1,
    pulseDurationFactor: isHovered ? 0.85 : 1,
    suppressSkip: Boolean(isHovered),
    planeBlur: isHovered ? abstractBlurRange.sharp : abstractBlurRange.soft,
  };
}
